/* eslint-disable react/no-unused-prop-types */
import { useCallback, useRef, useImperativeHandle } from 'react';
import { t } from '@lingui/macro';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { filterPropTypes, filterPropDefaults } from '../../page-framework/filter/FilterPropTypes';
import DataTable from '../../data-table/DataTable';
import { SELECTIONMODE_MULTI } from '../../data-table/SelectionContext';
import { convertLoadParamsToQueryParams } from '../../data-table/Loaders';
import UserFilterStateServiceFactory from './UserFilterStateService';
import { useDataService } from '../../../services/ApiDataService';
import Filter from '../../page-framework/filter/FilterDecorator';
import {
    FIRST_NAME_FIELD,
    LAST_NAME_FIELD,
    ROLE_NAMES,
    USER_NAME_FIELD,
    USER_ROLE_FIELD,
} from '../../pages-admin/users/UserConstants';

export const filterId = 'users';
export const filterTitle = t`Users`;

const dataSourceParams = {
    searchFields: [USER_NAME_FIELD, USER_ROLE_FIELD],
};
const columns = [
    {
        mapping: USER_NAME_FIELD,
        title: t`User Name`,
        sortable: true,
        width: 200,
    },
    {
        mapper: (row) => _.trim(`${_.get(row, FIRST_NAME_FIELD, '')} ${_.get(row, LAST_NAME_FIELD, '')}`),
        title: t`Full Name`,
        sortProperty: FIRST_NAME_FIELD,
        sortable: true,
        width: 200,
    },
    {
        mapping: USER_ROLE_FIELD,
        title: t`Role`,
        sortable: true,
        formatter: (role) => ROLE_NAMES[role],
        width: 130,
    },
];
const dataSource = {
    url: '/ipm/v1/admin/users',
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [USER_NAME_FIELD, FIRST_NAME_FIELD, LAST_NAME_FIELD, USER_ROLE_FIELD],
    },
};

function UserFilter({ state, innerRef }) {
    const innerStateRef = useRef(state || { inverted: true, selection: [] });
    const userCacheRef = useRef({});
    const globalUserCountRef = useRef(0);
    useImperativeHandle(innerRef, () => ({
        getCurrentState: () => {
            const { inverted, selection } = innerStateRef.current;
            let newSelection = selection.filter((id) => id in userCacheRef.current);
            newSelection = inverted
                ? Object.keys(userCacheRef.current).filter((id) => !newSelection.includes(id))
                : newSelection;
            if (newSelection.length === globalUserCountRef.current) {
                return { inverted: true, selection: [] };
            }

            const names = newSelection.map((id) => userCacheRef.current[id]);
            return { inverted: false, selection: newSelection, names };
        },
    }));

    const dataService = useDataService();

    const handleSelectionChange = useCallback(({ inverted, selection }) => {
        innerStateRef.current = {
            inverted,
            selection,
        };
    }, []);

    const loadUsers = useCallback(
        (loaderParams) => {
            const params = {
                ...dataSourceParams,
                ...convertLoadParamsToQueryParams(loaderParams),
            };
            return dataService.get(dataSource.url, params).then((res) => {
                const users = res?.data || [];
                const count = res?.count || 0;
                userCacheRef.current = _.mapValues(_.keyBy(users, '_id'), (u) => u.userName);
                if (!params.search) {
                    globalUserCountRef.current = count;
                }
                return { rows: users, length: count };
            });
        },
        [dataService]
    );

    return (
        <DataTable
            allowSelectAll
            disableCSVExport
            disableColumnRemover
            visibleRows={5}
            limit={5000}
            showHeaders
            showSelectionColumn
            shrink={false}
            loader={loadUsers}
            columns={columns}
            selection={innerStateRef.current}
            selectionOptions={{
                mode: SELECTIONMODE_MULTI,
                idMapping: '_id',
                allowSelectAll: true,
                onChange: handleSelectionChange,
            }}
        />
    );
}

UserFilter.propTypes = {
    filterActions: filterPropTypes.filterActions,
    id: filterPropTypes.id,
    title: filterPropTypes.title,
    state: PropTypes.shape({
        inverted: PropTypes.bool,
        selection: PropTypes.arrayOf(PropTypes.string),
    }),
    innerRef: PropTypes.any,
};

UserFilter.defaultProps = {
    ...filterPropDefaults,
    id: filterId,
    maxWidth: 'md',
    title: filterTitle,
    state: {
        inverted: true,
        selection: [],
    },
    innerRef: undefined,
};

export default Filter({
    filterId,
    filterStateServiceFactory: UserFilterStateServiceFactory,
})(UserFilter);
